import { getDateFromAge, getFirstOfNextMonth } from '../general/utils/date';
import { InsuranceCategory, InsuranceGroup } from '../types/consultation';
import { animatePriceField } from './plugins/animateNumber';
import { debounce } from './utils/utils';
import { pushToDataLayer } from './utils/ga4-tracking/push-to-data-layer';
import { TariffBundleNames, tariffBundleMarketingNames } from '../types/tariffs';

declare global {
  const backendApiUrl: string;
}

type TariffData = {
  ingress_date: string;
  name: string;
  premium: number;
  surcharges: any[];
};

type DentalTariffData = {
  ingress_date: string;
  name: string;
  surcharges: any[];
  premium: {
    base: number;
  };
};

// HARDCODED TARIFF DETAILS
const tariffs = [
  {
    tariffBundle: 'economy',
    tariffName: 'Premium Economy',
    deductible: en ? '€500' : '500 €',
    benefits: [en ? 'Double room incl. head of department' : '2-Bett Zimmer inkl. Chefarzt', en ? '60 - 80% for dental prostheses' : '60 - 80 % für Zahnersatz']
  },
  {
    tariffBundle: 'business2019twin',
    tariffName: 'Business Class',
    deductible: en ? '€500' : '500 €',
    benefits: [en ? 'Double room incl. head of department' : '2-Bett Zimmer inkl. Chefarzt', en ? '80 - 90% for dental prostheses' : '80 - 90 % für Zahnersatz']
  },
  {
    tariffBundle: 'businesspro2024',
    tariffName: 'Business Class <span class="pro-badge">Pro</span>',
    deductible: en ? '€0' : '0 €',
    benefits: [
      en
        ? 'No-claim bonus <span class="tooltip info" data-tooltip="You have the opportunity to get part of your premiums back (up to 1 annual average monthly premium) if you don\'t claim any benefits other than preventive check-ups (statutory programs), professional dental cleanings and vaccinations (except travel vaccinations) for a year." tabindex="0"></span>'
        : 'Beitragsrückerstattung <span class="tooltip info" data-tooltip="Wenn du ein Jahr lang keine Leistungen außer Vorsorgeuntersuchungen (gesetzl. Programme), professionelle Zahnreinigung und Impfungen (außer Auslandsimpfungen) einreichst, erhältst du einen Teil deiner Beiträge (bis zu 1 Jahres-Durchschnitts-Monatsbeitrag) zurückerstattet. "tabindex="0"></span>',
      en ? 'Double room incl. head of department' : '2-Bett Zimmer inkl. Chefarzt',
      en ? '80 - 90% for dental prostheses' : '80 - 90 % für Zahnersatz'
    ]
  },
  {
    tariffBundle: 'first2019single',
    tariffName: 'First Class',
    deductible: en ? '€500' : '500 €',
    benefits: [en ? 'Private room incl. head of department' : '1-Bett Zimmer inkl. Chefarzt', en ? '90% for dental prostheses' : '90 % für Zahnersatz']
  },
  {
    tariffBundle: 'firstproplus2024',
    tariffName: 'First Class <span class="pro-badge">Pro+</span>',
    deductible: en ? '€0' : '0 €',
    benefits: [
      en
        ? 'No-claim bonus <span class="tooltip info" data-tooltip="If you do not submit any claims for a year except for preventive check-ups (statutory programs), professional dental cleaning and vaccinations (except for vaccinations abroad), you will receive a refund of part of your contributions (up to 1 annual average monthly premium)" tabindex="0"></span>'
        : 'Beitragsrückerstattung <span class="tooltip info" data-tooltip="Wenn du ein Jahr lang keine Leistungen außer Vorsorgeuntersuchungen (gesetzl. Programme), professionelle Zahnreinigung und Impfungen (außer Auslandsimpfungen) einreichst, erhältst du einen Teil deiner Beiträge zurückerstattet (bis zu 1 Jahres-Durchschnitts-Monatsbeitrag)" tabindex="0"></span>',
      en ? '9 months free of contribution during parental leave' : '9 Monate beitragsfrei bei Elterngeldbezug',
      en ? 'Private room incl. head of department' : '1-Bett Zimmer inkl. Chefarzt',
      en ? '90% for dental prostheses' : '90 % für Zahnersatz'
    ]
  },
  {
    tariffBundle: 'chi-student-smart',
    tariffName: 'Study Smart',
    deductible: en ? '€0' : '0 €',
    benefits: [
      en ? 'Accommodation in double room' : '2-Bett Zimmer inkl. Chefarzt',
      en ? '€80 per year for professional dental cleaning' : '80 € im Jahr für eine professionelle Zahnreinigung',
      en ? '€100 within 60 months for visual aids' : '100 € alle 60 Monate für Sehhilfen',
      en ? 'Continuation in another tariff without health check after the studies' : 'Wechseloption ohne erneute Gesundheitsprüfung nach dem Studium'
    ]
  },
  {
    tariffBundle: 'chi-student-protect',
    tariffName: 'Study Protect',
    deductible: en ? '€500' : '500 €',
    benefits: [
      en ? 'Accommodation in double room' : '2-Bett Zimmer inkl. Chefarzt',
      en ? '€ 125 per year for professional dental cleaning' : '125 € im Jahr für eine professionelle Zahnreinigung',
      en ? '€ 550 within 36 months for visual aids' : '550 € alle 36 Monate für Sehhilfen',
      en ? 'Continuation in another tariff without health check after the studies' : 'Wechseloption ohne erneute Gesundheitsprüfung nach dem Studium'
    ]
  },
  {
    tariffBundle: 'chi-student-free',
    tariffName: 'Study Free',
    deductible: en ? '€0' : '0 €',
    benefits: [
      en ? 'Accommodation in double room' : '2-Bett Zimmer inkl. Chefarzt',
      en ? '€ 125 per year for professional dental cleaning' : '125 € im Jahr für eine professionelle Zahnreinigung',
      en ? '€ 550 within 36 months for visual aids' : '550 € alle 36 Monate für Sehhilfen',
      en ? 'Continuation in another tariff without health check after the studies' : 'Wechseloption ohne erneute Gesundheitsprüfung nach dem Studium'
    ]
  },
  {
    tariffBundle: 'expat',
    tariffName: 'First Class Expats',
    deductible: en ? '€500' : '500 €',
    benefits: [
      en ? 'English-support-team' : 'Englischsprachiges Support-Team',
      en ? 'Dental treatment 100%' : 'bis zu 100 % für Zahnersatz',
      en ? 'Accommodation: 1 bed room' : '1-Bett Zimmer inkl. Chefarzt'
    ]
  }
];

document.querySelectorAll('.qqc-breakdown-v2-block').forEach(block => {
  // FORM
  const qqcForm = block.querySelector('form.pkvQQC');
  const ageInput = qqcForm?.querySelector('input.ageInput') as HTMLInputElement;
  const employmentSelect = qqcForm?.querySelector('select.employmentSelect') as HTMLSelectElement;
  const incomeSelect = qqcForm?.querySelector('select.incomeSelect') as HTMLSelectElement;
  let jaeg: string, jaegReachedNumber: string, jaegNotReachedNumber: string, selfEmployedLimit: string, overOption: HTMLOptionElement, underOption: HTMLOptionElement;
  if (incomeSelect && incomeSelect.dataset.jaeg) {
    jaeg = incomeSelect.dataset.jaeg;
    selfEmployedLimit = '30.000';
    overOption = incomeSelect?.querySelector('option[value="over"]') as HTMLOptionElement;
    underOption = incomeSelect?.querySelector('option[value="under"]') as HTMLOptionElement;
    jaegReachedNumber = (Number(jaeg.replace(en ? ',' : '.', '')) + 1).toString();
    jaegNotReachedNumber = (Number(jaeg.replace(en ? ',' : '.', '')) - 1).toString();
  }

  const jeagTooltip = qqcForm?.querySelector('.tooltip.jaeg') as HTMLElement;
  const interestSelectWrapper = qqcForm?.querySelector('.select-wrapper.interest') as HTMLElement;
  const interestSelect = interestSelectWrapper?.querySelector('select.interestSelect') as HTMLSelectElement;
  let selectedTariff = (block.querySelector('.qqc') as HTMLElement)?.dataset.initTariffChoice;
  if (selectedTariff === 'expat' && interestSelect) {
    selectedTariff = 'businesspro2024';
  }
  const initTariff = selectedTariff;

  if (interestSelect) {
    Array.from(interestSelect.querySelectorAll('option')).forEach(option => {
      if (option.value === selectedTariff) {
        option.setAttribute('selected', '');
      }
    });
  }

  // TOP UP TARIF CONTAINER
  const topUpTariffContainer = block.querySelector('.topup-tariff-container') as HTMLElement;

  // TOP UP CONTAINER
  const topupContainer = block.querySelector('.topupContainer') as HTMLElement;
  let dentalPrice: HTMLElement | undefined, clinicPrice: HTMLElement | undefined;
  if (topUpTariffContainer) {
    dentalPrice = topupContainer?.querySelector('.dental-price') as HTMLElement;
    clinicPrice = topupContainer?.querySelector('.clinic-price') as HTMLElement;
  }
  const topUpTextEmployed = topupContainer?.querySelector('.no-offers .employed') as HTMLElement;
  const topUpTextSelfEmployed = topupContainer?.querySelector('.no-offers .self-employed') as HTMLElement;

  // TARIFF CARD
  const tariffButtonsWrapper = block.querySelector('.tariff-buttons-wrapper') as HTMLElement;
  const tariffCard = tariffButtonsWrapper.querySelector('.tariff') as HTMLElement;
  const tariffName = tariffCard?.querySelector('.tariff-name') as HTMLElement;
  const contribution = tariffCard?.querySelector('.contribution > div') as HTMLElement;
  const deducBreGkvAdvantagewrapper = tariffCard?.querySelector('.deductible-bre-gkv-advantage-wrapper') as HTMLElement;
  const gkvAverage = deducBreGkvAdvantagewrapper?.dataset.averageGkvNormal;
  const gkvAverageUnder = deducBreGkvAdvantagewrapper?.dataset.averageGkvUnder;
  const gkvMax = deducBreGkvAdvantagewrapper?.dataset.averageMaxGkv;
  let maxEmployerKv: string | number | undefined = deducBreGkvAdvantagewrapper?.dataset.maxEmployerKv;
  let maxEmployerPv: string | number | undefined = deducBreGkvAdvantagewrapper?.dataset.maxEmployerPv;
  if (maxEmployerKv && maxEmployerPv) {
    maxEmployerKv = parseFloat(maxEmployerKv.replace(/[,.]/g, ''));
    maxEmployerPv = parseFloat(maxEmployerPv.replace(/[,.]/g, ''));
  }
  const deductible = deducBreGkvAdvantagewrapper?.querySelector('.deductible strong') as HTMLElement;
  const contributionRefund = deducBreGkvAdvantagewrapper?.querySelector('.bre') as HTMLElement;
  let contributionRefundNum: HTMLElement | null = null;
  if (contributionRefund) {
    contributionRefundNum = contributionRefund?.querySelector('strong');
  }
  const benefitsList = tariffCard?.querySelector('.otto-list') as HTMLElement;
  const gkvSavings = tariffCard?.querySelector('.savings') as HTMLElement;
  let gkvSavingsNum: HTMLElement | null = null;
  let gkvTooltips: NodeListOf<HTMLElement> | null = null;
  if (gkvSavings) {
    gkvSavingsNum = gkvSavings?.querySelector('.savings .number') as HTMLElement;
    gkvTooltips = gkvSavings.querySelectorAll('.tooltip');
  }
  const noRecommendation = tariffCard?.querySelector('.no-recommendation') as HTMLElement;
  let gkvContribution = 0;

  // STUDENT
  const bafogSwitch = tariffCard?.querySelector('.bafog.switch input') as HTMLInputElement;
  const bafogTextWrapper = tariffCard.querySelector('.bafog-text-wrapper') as HTMLElement;
  let underText: HTMLElement | undefined, overText: HTMLElement | undefined, bafogHr: HTMLHRElement | undefined;
  if (bafogTextWrapper) {
    underText = bafogTextWrapper.querySelector('.under') as HTMLElement;
    overText = bafogTextWrapper.querySelector('.over') as HTMLElement;
    bafogHr = bafogTextWrapper.querySelector('.hr') as HTMLHRElement;
  }

  // OTHER
  const apiUrl = backendApiUrl + '/tariff';
  const mediaMatcherMinXxs = window.matchMedia('(min-width: 400px)');
  const mediaMatcherMinXs = window.matchMedia('(min-width: 575px)');
  const mediaMatcherMinSm = window.matchMedia('(min-width: 768px)');
  const buttons = block.querySelectorAll('a.btn');
  let firstQqcInteraction = false;
  let ageCheck = parseInt(ageInput.value);

  let tariffBundleName: TariffBundleNames | undefined;

  // GET TOOLTIP EVENT
  let tooltipReceived = false;
  window.addEventListener('tooltips-loaded', () => {
    tooltipReceived = true;
  });

  ageInput?.addEventListener('input', () => {
    const age = parseInt(ageInput.value);
    if (incomeSelect || (!incomeSelect && age > 17 && age < 39)) {
      runQqc();
    }

    if (dentalPrice && clinicPrice && !topupContainer.classList.contains('hidden') && ageInput.value.length === 2) {
      setTopUpValues();
    }

    if (inputsAreValid()) {
      if (!firstQqcInteraction) {
        sendQqcEvent();
        firstQqcInteraction = true;
      }
      if (bafogSwitch) {
        underText?.classList.toggle('hide', !(bafogSwitch.checked && age < 30));
        overText?.classList.toggle('hide', !(bafogSwitch.checked && age > 29));
      }
      if (((age > 55 && ageCheck < 56) || (age < 56 && ageCheck > 55)) && incomeSelect && selectedTariff !== 'expat') {
        gkvSavings.classList.toggle('active', parseInt(ageInput.value) < 55);
        noRecommendation.classList.toggle('active', parseInt(ageInput.value) > 55);
        ageCheck = age;
        setTopUpTariffContainerHeight();
      }
    }
  });

  employmentSelect?.addEventListener('change', () => {
    runQqc();
    updateIncomeSelect();
    if (gkvTooltips) {
      gkvTooltips.forEach(tooltip => {
        tooltip.classList.toggle('active', tooltip.classList.contains(employmentSelect.value));
      });
    }
    if (!firstQqcInteraction) {
      sendQqcEvent();
      firstQqcInteraction = true;
    }
  });

  incomeSelect?.addEventListener('change', () => {
    setTariff();
    handleTopUpContainer();
    if (!firstQqcInteraction) {
      sendQqcEvent();
      firstQqcInteraction = true;
    }
  });

  if (interestSelect) {
    interestSelect?.addEventListener('change', async () => {
      await setTariff(interestSelect.value);
      if (!selectedTariff?.includes('student') || en) {
        setInterestSelectStyles();
      }
      if (!firstQqcInteraction) {
        sendQqcEvent();
        firstQqcInteraction = true;
      }
    });
  }

  if (bafogSwitch) {
    bafogSwitch?.addEventListener('change', () => {
      handleBafogSwitch();
    });
  }

  async function runQqc(): Promise<void> {
    if (!inputsAreValid()) {
      setButtonAttributes();
      return;
    }
    const age = ageInput.value;
    let employment: string | undefined;
    if (employmentSelect) {
      employment = employmentSelect.value;
    }

    const tariffData = await getTariffData(parseInt(age), employment ? employment : undefined);
    let tariffPremium: boolean | number = false;
    let premiumPlain: boolean | number = false;
    let nursingCareShare: boolean | number = false;
    if (typeof tariffData !== 'boolean') {
      tariffPremium = tariffData?.tariffPremium;
      premiumPlain = tariffData?.premiumPlain;
      nursingCareShare = tariffData?.nursingCareShare;
      tariffBundleName = tariffData?.tariffBundleName;
    }
    if (typeof tariffPremium === 'number' && typeof nursingCareShare === 'number') {
      // Monthly contribution
      if ((employmentSelect && employmentSelect.value === 'self-employed' && typeof premiumPlain === 'number') || !employmentSelect) {
        tariffPremium /= 100;
      } else {
        if (typeof maxEmployerKv === 'number' && typeof maxEmployerPv === 'number') {
          const tariffShare = Math.min((tariffPremium - nursingCareShare) / 2, maxEmployerKv);
          const pvShare = Math.min(nursingCareShare / 2, maxEmployerPv);
          tariffPremium = Math.floor(tariffPremium - (tariffShare + pvShare)) / 100;
        }
      }
      if (employmentSelect) {
        // Yearly savings
        let preChoice = gkvAverage;
        if (employmentSelect.value === 'self-employed') {
          preChoice = gkvMax;
        } else if (parseInt(ageInput.value) < 23) {
          preChoice = gkvAverageUnder;
        }
        if (preChoice) {
          gkvContribution = parseInt(preChoice.replace(/[,.]/g, '')) / (preChoice.length < 4 ? 1 : 100);
        }
        const gkvContributionSavings = (gkvContribution - tariffPremium) * 12;
        gkvSavings.classList.toggle('no-savings', gkvContributionSavings < 0);
        if (gkvContributionSavings > 0) {
          animatePriceField(gkvSavingsNum, gkvContributionSavings);
        }
        if (typeof premiumPlain === 'number' && selectedTariff?.includes('pro') && (employmentSelect.value === 'employed' || employmentSelect.value === 'self-employed')) {
          const contributionRefundAmount = Math.round(premiumPlain) / 100 / 1.1;
          if (contributionRefundNum) {
            animatePriceField(contributionRefundNum, contributionRefundAmount);
          }
        }
      }

      animatePriceField(contribution, tariffPremium);
      setButtonAttributes();
    }
  }

  async function getTariffData(age: number, type?: string): Promise<{ tariffPremium: number; premiumPlain: number; nursingCareShare: number; tariffBundleName?: TariffBundleNames } | boolean> {
    let endpoint: string;
    if (incomeSelect) {
      const dsa = type === 'employed' ? '43' : '42';
      const expatParam = `&is_non_eu=1`;
      const contributionRelief = (age < 21 ? '' : '&contribution_relief_from_age=67') + '&contribution_relief=100';
      endpoint = `/otto/quotes?occupational_status=${type}&date_of_birth=${getDateFromAge(age)}&dsa_after_days=${dsa}&dsa_requested_payout=120${selectedTariff?.includes('expat') ? expatParam : ''}${
        !selectedTariff?.includes('expat') && age < 56 ? contributionRelief : ''
      }&ingress_date=${getFirstOfNextMonth()}&tariff_name=${selectedTariff}&tariff_excess=${selectedTariff?.includes('pro') ? '0' : '10'}`;
    } else {
      endpoint = `/otto/student/quotes?date_of_birth=${getDateFromAge(age)}&ingress_date=${getFirstOfNextMonth()}&tariff_name=${selectedTariff}`;
    }
    try {
      const response = await fetch(apiUrl + endpoint);
      if (!response.ok) {
        return false;
      }
      const data: TariffData[] = await response.json();

      const tariffBundleName = data.find(d => d.name.includes(selectedTariff!))?.name as TariffBundleNames;
      const premiumPlain = data.find(d => d.name === tariffBundleName)?.premium || 0;
      const nursingCareShare = data.find(d => d.name.includes('cnci-normal'))?.premium || 0;
      let tariffData = data.map(tariff => tariff.premium);
      let tariffPremium = 0;
      tariffData.forEach(premium => {
        tariffPremium += premium;
      });
      return { tariffPremium, premiumPlain, nursingCareShare, tariffBundleName };
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async function setTariff(newTariff?: string): Promise<void> {
    if (newTariff) {
      selectedTariff = newTariff;
    }
    if (!inputsAreValid()) {
      setButtonAttributes();
      return;
    }
    benefitsList.innerHTML = '';
    await runQqc();

    const tariff = tariffs.find(tariff => tariff.tariffBundle === selectedTariff);
    if (tariff) {
      tariffCard?.setAttribute('tariff', selectedTariff!);
      const newTariffName = tariff.tariffName;
      tariffName.classList.add('move-out');
      setTimeout(() => {
        tariffName.innerHTML = newTariffName;
        tariffName.classList.remove('move-out');
      }, 300);
      if (incomeSelect) {
        contributionRefund.classList.toggle('hide', !selectedTariff?.includes('pro'));
      }
      deductible.innerHTML = tariff.deductible;
      tariff.benefits.forEach(benefit => {
        const benefitElement = document.createElement('li');
        benefitElement.classList.add('otto-list-item', 'move-in');
        if (tariff.tariffBundle.includes('first') || tariff.tariffBundle.includes('free')) {
          benefitElement.classList.add('gold');
        } else if (tariff.tariffBundle.includes('expat')) {
          benefitElement.classList.add('expats');
        }
        benefitElement.innerHTML = benefit;
        const tooltip = benefitElement.querySelector('.tooltip') as HTMLElement;
        if (tooltip && tooltipReceived) {
          initTooltip(tooltip);
        }
        benefitsList.appendChild(benefitElement);
      });
      let timeOut = 0;
      benefitsList.querySelectorAll('li').forEach(li => {
        setTimeout(() => {
          li.classList.remove('move-in');
        }, timeOut);
        timeOut += 150;
      });
      setTopUpTariffContainerHeight();
    }
  }
  if (bafogSwitch) {
    handleBafogSwitch();
  }
  setTariff();

  // ALL THE HELPERS
  function inputsAreValid() {
    const age = parseInt(ageInput.value);
    return age > 17 && age < 69 && (incomeSelect ? incomeSelect.value === 'over' : true);
  }

  async function handleTopUpContainer() {
    if (incomeSelect) {
      topupContainer?.classList.toggle('hidden', incomeSelect.value === 'over');
      tariffButtonsWrapper?.classList.toggle('hidden', incomeSelect.value === 'under');
    }
    if (dentalPrice && clinicPrice && !topupContainer.classList.contains('hidden')) {
      setTopUpValues();
    }
    setTopUpTariffContainerHeight();
  }

  async function setTopUpValues() {
    const dentalPremium = await getTopUpTariffPremium('dental');
    const clinicPremium = await getTopUpTariffPremium('clinic');
    if (typeof dentalPremium === 'number' && typeof clinicPremium === 'number' && clinicPrice && dentalPrice) {
      animatePriceField(clinicPrice, clinicPremium);
      animatePriceField(dentalPrice, dentalPremium);
    }
  }

  async function getTopUpTariffPremium(topUpType: string): Promise<number | undefined | boolean> {
    let url = `${apiUrl}/otto/`;
    if (topUpType === 'clinic') {
      url += `clinic/quotes?date_of_birth=${getDateFromAge(parseInt(ageInput.value))}`;
    } else {
      url += `dental/tariffs?tariff_name=economy-v2&ingress_date=${getFirstOfNextMonth()}&date_of_birth=${getDateFromAge(
        parseInt(ageInput.value)
      )}&missing_teeth=0&agent_verified_ingress_date_change=0`;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        return false;
      }
      const data: TariffData[] | DentalTariffData[] = await response.json();

      const tariffData = data.find(tariff => tariff.name === (topUpType === 'dental' ? 'tud-economy-v2' : 'tuc-zweibett'));

      if (!tariffData) {
        return false;
      }
      const tariffPremium = topUpType === 'dental' ? (tariffData as DentalTariffData).premium.base : (tariffData as TariffData).premium;
      return tariffPremium / 100;
    } catch (error) {
      console.error(error);
    }
  }

  function setTopUpTariffContainerHeight() {
    if (topUpTariffContainer) {
      topUpTariffContainer.style.height = `${!incomeSelect || incomeSelect.value === 'over' ? tariffButtonsWrapper?.offsetHeight : topupContainer?.offsetHeight}px`;
    }
  }

  function setInterestSelectStyles() {
    let interestSelectWrapperWidth = 'auto';
    if (en && selectedTariff?.includes('student')) {
      if (mediaMatcherMinSm.matches) {
        interestSelectWrapperWidth = selectedTariff === 'chi-student-smart' ? '50%' : selectedTariff === 'chi-student-protect' ? '55%' : 'auto';
      } else if (mediaMatcherMinXs.matches) {
        interestSelectWrapperWidth = selectedTariff === 'chi-student-smart' ? '65%' : selectedTariff === 'chi-student-protect' ? '70%' : 'auto';
      }
    } else if (!selectedTariff?.includes('business') && !selectedTariff?.includes('student')) {
      if (mediaMatcherMinSm.matches) {
        interestSelectWrapperWidth = '50%';
      } else if (mediaMatcherMinXs.matches) {
        interestSelectWrapperWidth = '65%';
      } else if (mediaMatcherMinXxs.matches) {
        interestSelectWrapperWidth = '75%';
      }
    }
    interestSelectWrapper.style.width = interestSelectWrapperWidth;
  }
  if (interestSelect && (!selectedTariff?.includes('student') || en)) {
    setInterestSelectStyles();
  }

  function updateIncomeSelect() {
    jeagTooltip.classList.toggle('hide', employmentSelect.value === 'self-employed');
    topUpTextEmployed.classList.toggle('active', employmentSelect.value === 'employed');
    topUpTextSelfEmployed.classList.toggle('active', employmentSelect.value === 'self-employed');

    const currentLimit = employmentSelect.value === 'employed' ? jaeg : selfEmployedLimit;
    overOption.innerHTML = en ? `over €${currentLimit}` : `über ${currentLimit}€`;
    underOption.innerHTML = en ? `under €${currentLimit}` : `unter ${currentLimit}€`;
  }
  if (incomeSelect) {
    updateIncomeSelect();
  }

  function handleBafogSwitch() {
    const age = parseInt(ageInput.value);
    if (bafogTextWrapper && bafogHr && overText && underText) {
      underText.classList.toggle('hide', !(bafogSwitch.checked && age < 30));
      overText.classList.toggle('hide', !(bafogSwitch.checked && age > 29));
      bafogHr.classList.toggle('hide', !bafogSwitch.checked);
      const hrHeight = parseFloat(getComputedStyle(bafogHr).borderTopWidth.replace('px', '')) + parseFloat(getComputedStyle(bafogHr).marginBottom.replace('px', '')) + bafogHr.offsetHeight;
      const textHeight = bafogSwitch.checked && parseFloat(ageInput.value) < 30 ? underText?.offsetHeight : overText?.offsetHeight;
      const wrapperHeight = textHeight + hrHeight;
      bafogTextWrapper.style.height = bafogSwitch.checked ? wrapperHeight + 'px' : '0px';
      topUpTariffContainer.style.height = `${bafogSwitch.checked ? tariffButtonsWrapper?.offsetHeight + wrapperHeight : tariffButtonsWrapper?.offsetHeight - wrapperHeight}px`;
    }
  }

  function setButtonAttributes() {
    buttons.forEach(button => {
      button.setAttribute('data-tr-job_status', bafogSwitch ? 'student' : employmentSelect.value);
      button.setAttribute('data-tr-yob', getDateFromAge(parseInt(ageInput.value)).substring(0, 4));
      button.setAttribute('data-tr-tariff', selectedTariff!);
      if (!bafogSwitch) {
        if (employmentSelect.value !== 'self-employed') {
          button.setAttribute('data-tr-jaeg_reached', incomeSelect.value === 'over' ? '1' : '0');
        }
        let incomeSeleteValue = employmentSelect.value === 'self-employed' ? '29999' : jaegNotReachedNumber;
        if (incomeSelect.value === 'over') {
          incomeSeleteValue = employmentSelect.value === 'self-employed' ? '30001' : jaegReachedNumber;
        }
        button.setAttribute('data-tr-income', incomeSeleteValue);
      }
      if (interestSelect) {
        button.setAttribute('data-tr-default_tariff', initTariff!);
      }
    });
  }

  function sendQqcEvent() {
    pushToDataLayer({
      event: 'quick_quote_calculated',
      element_section: block.classList.contains('qqc-hero') ? 'qqc-breakdown-hero-v2-block' : 'qqc-breakdown-v2-block',
      tariff: tariffBundleName ? tariffBundleMarketingNames[tariffBundleName] : undefined,
      insurance_category: 'full' as InsuranceCategory,
      insurance_group: (bafogSwitch ? 'student' : 'employee_self-employed') as InsuranceGroup
    });
  }

  window.addEventListener(
    'resize',
    debounce(() => {
      if (interestSelect && (!selectedTariff?.includes('student') || en)) {
        setInterestSelectStyles();
      }
      if (bafogSwitch) {
        handleBafogSwitch();
      }
      setTopUpTariffContainerHeight();
    }, 300)
  );
  //RESET FORM ON BROWSER BACK
  window.addEventListener('pageshow', () => {
    (qqcForm as HTMLFormElement)?.reset();
  });
});
